import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from 'primereact/button'

import { CustomConfirmDialog } from '../../../../../../components/dialogs'
import { useToastContext } from '../../../../../../contexts/toast'
import { requestAttachmentDelete } from '../../../../data/api'

type DeleteAttachmentButtonProps = {
  attachmentId: string
  attachmentUrl: string
  attachmentName: string
  invoiceId: string
}

export const DeleteAttachmentDialog: React.FC<DeleteAttachmentButtonProps> = ({
  attachmentId,
  attachmentUrl,
  attachmentName,
  invoiceId,
}) => {
  const { t } = useTranslation()
  const { showToast } = useToastContext()
  const [isRequestRunning, setIsRequestRunning] = useState(false)
  const [isVisible, setIsVisible] = useState(false)

  const handleShow = () => setIsVisible(true)
  const handleHide = () => setIsVisible(false)

  const handleDelete = async () => {
    setIsRequestRunning(true)

    try {
      await requestAttachmentDelete(invoiceId, attachmentId, attachmentUrl)

      showToast({
        severity: 'success',
        summary: t(
          'invoice.attachments.table.delete.dialog.delete.messages.success.summary',
        ),
        detail: t(
          'invoice.attachments.table.delete.dialog.delete.messages.success.detail',
          { name: attachmentName },
        ),
        life: 3000,
      })
    } catch (error) {
      showToast({
        severity: 'error',
        summary: t(
          'invoice.attachments.table.delete.dialog.delete.messages.failure.summary',
        ),
        detail: t(
          'invoice.attachments.table.delete.dialog.delete.messages.failure.detail',
          { name: attachmentName },
        ),
        sticky: true,
      })
    } finally {
      setIsRequestRunning(false)
    }
  }

  return (
    <>
      <Button
        icon={'pi pi-trash'}
        className="p-button-rounded p-button-text"
        severity="danger"
        onClick={handleShow}
        loading={isRequestRunning}
      />

      <CustomConfirmDialog
        severity="danger"
        isVisible={isVisible}
        header={t('invoice.attachments.table.delete.dialog.title')}
        message={t('invoice.attachments.table.delete.dialog.description', {
          attachment: attachmentName,
        })}
        onHide={handleHide}
        onReject={handleHide}
        onAccept={handleDelete}
        acceptLabel={t('invoice.attachments.table.delete.dialog.delete.label')}
        rejectLabel={t('invoice.attachments.table.delete.dialog.cancel.label')}
      />
    </>
  )
}
